import {DashboardCard} from '../dashboard-card/DashboardCard'
import {useTranslation, Trans} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useGetDashboardConsultants} from '../../query/useGetDashboardConsultants'
import {DashboardConsultantSearchParams} from '../../types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {DashboardConsultantCardEmptyState} from './DashboardConsultantCardEmptyState'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {DashboardConsultantHoursStats} from '../dashboard-consultant-hours-stats/DashboardConsultantHoursStats'
import {useMemo} from 'react'
import {formatNumber} from '@/utilities/helpers'

export const DashboardConsultantHoursCard = () => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(DashboardConsultantSearchParams)
    const {data, isLoading} = useGetDashboardConsultants(
        {
            fields: ['hours'],
            ...searchParams
        },
        {enabled: !!searchParams.consultant}
    )

    const hasConsultant = !!searchParams.consultant

    const difference = useMemo(
        () => (data?.hours?.done ?? 0) - (data?.hours?.planned ?? 0),
        [data?.hours?.done, data?.hours?.planned]
    )

    const absenceHours = useMemo(() => {
        return (data?.hours?.absences ?? 0) / 8
    }, [data?.hours?.absences])

    const absenceDays = useMemo(() => {
        return Math.floor(absenceHours)
    }, [absenceHours])

    const remainingAbsenceHours = useMemo(() => {
        return Math.round((absenceHours - absenceDays) * 8)
    }, [absenceHours, absenceDays])

    return (
        <DashboardCard title={t('dashboard:hour_count')}>
            {!hasConsultant ? (
                <DashboardConsultantCardEmptyState />
            ) : isLoading ? (
                <Flexbox direction="column" gap={4} fullWidth>
                    <Skeleton width="40%" height={28} />
                    <Flexbox direction="column" gap={2}>
                        <Skeleton width="70%" height={16} />
                        <Skeleton width="80%" height={16} />
                    </Flexbox>
                </Flexbox>
            ) : (
                <Flexbox direction="column" gap={4} fullWidth>
                    <DashboardConsultantHoursStats />
                    <ul>
                        <li>
                            <Flexbox align="center" gap={1}>
                                <Text
                                    palette={difference > 0 ? 'success' : 'danger'}
                                    shade="500"
                                    size="textSm"
                                    weight={600}
                                >
                                    {formatNumber(difference)}
                                </Text>
                                <Text palette="neutral" shade="600" size="textSm" weight={400}>
                                    {t('dashboard:difference_from_budget')}
                                </Text>
                            </Flexbox>
                        </li>
                        <li>
                            <Text palette="neutral" shade="600" size="textSm" weight={400}>
                                {absenceDays > 0 && remainingAbsenceHours > 0 ? (
                                    <Trans
                                        i18nKey="dashboard:absence_days"
                                        count={absenceDays}
                                        values={{value: formatNumber(absenceDays)}}
                                        components={[
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="900"
                                                size="textSm"
                                                weight={600}
                                            />
                                        ]}
                                    />
                                ) : absenceDays > 0 ? (
                                    <Trans
                                        i18nKey="dashboard:absence_days"
                                        count={absenceDays}
                                        values={{value: formatNumber(absenceDays)}}
                                        components={[
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="900"
                                                size="textSm"
                                                weight={600}
                                            />
                                        ]}
                                    />
                                ) : (
                                    <Trans
                                        i18nKey="dashboard:absence_hours"
                                        count={
                                            absenceDays === 0 && remainingAbsenceHours === 0 ? 0 : remainingAbsenceHours
                                        }
                                        values={{
                                            value: formatNumber(
                                                absenceDays === 0 && remainingAbsenceHours === 0
                                                    ? 0
                                                    : remainingAbsenceHours
                                            )
                                        }}
                                        components={[
                                            <Text
                                                render="span"
                                                palette="neutral"
                                                shade="900"
                                                size="textSm"
                                                weight={600}
                                            />
                                        ]}
                                    />
                                )}
                                {absenceDays > 0 && remainingAbsenceHours > 0 && (
                                    <Text render="span" palette="neutral" shade="600" size="textSm" weight={400}>
                                        {' '}
                                        ({formatNumber(remainingAbsenceHours)} {t('dashboard:hours')})
                                    </Text>
                                )}
                            </Text>
                        </li>
                    </ul>
                </Flexbox>
            )}
        </DashboardCard>
    )
}
